import { Logo } from "@storytell/core";
import { FaSolidGraduationCap } from "solid-icons/fa";
import { RiUserFacesUserStarLine } from "solid-icons/ri";
import {
  TbArchive,
  TbArrowsDiagonal,
  TbBell,
  TbBolt,
  TbBrain,
  TbCheck,
  TbChevronDown,
  TbChevronsRight,
  TbCopy,
  TbCube,
  TbDeviceDesktop,
  TbDots,
  TbEdit,
  TbFilter,
  TbFilterPlus,
  TbGift,
  TbHelp,
  TbInfoCircle,
  TbLayoutGrid,
  TbLayoutList,
  TbLayoutSidebar,
  TbListCheck,
  TbLoader2,
  TbMicrophone,
  TbPin,
  TbPinFilled,
  TbPlus,
  TbPuzzle,
  TbSearch,
  TbSettings,
  TbSquare,
  TbTag,
  TbTrash,
  TbUpload,
  TbWaveSquare,
  TbWorld,
} from "solid-icons/tb";
import { type Component, For, type JSX, type ParentComponent, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { LuLibrary } from "~/components/icons/LuLibrary";
import { MemoryCountIndicator } from "~/components/memory/MemoryCountIndicator";
import { MemoryEntry } from "~/components/memory/MemoryEntry";
import { MemoryToggle } from "~/components/memory/MemoryToggle";
import { getAssetIcon, getAssetIconColor } from "~/components/utils/getAssetIcon";
import { assetLifecycleStatusPillClasses } from "~/components/utils/lifecycleStateMappers";
import { useI18n } from "~/i18n";
import { ASSET_SOURCE_ICONS, INTEGRATION_PROVIDER_ICONS } from "~/lib/integrations/integrationProviderIcons";
import { INGESTING_PROVIDERS, INTEGRATION_PROVIDERS } from "~/lib/integrations/integrationProviders";
import type { LibraryData, LibraryFile, LibraryMemory } from "../LibraryMockup";
import { at, Cursor } from "./parts";

/**
 * When each beat starts, in seconds on screen. Entrances read about a quarter
 * second after their delay.
 */
export const T = { typing: 0.5, filter: 1.0, landed: 2.2, ready: 3.2, saved: 4.0, cursor: 4.7, pin: 5.7 };

/** A nav row: a borderless button that fills while its page is open. */
const NAV_ROW = "flex w-full items-center justify-start gap-1.5 rounded-lg py-2 pr-1.5 text-sm whitespace-nowrap";

/** The shared kit's default button: panel fill, hairline border. */
const BUTTON =
  "flex items-center justify-center gap-1.5 rounded-lg border border-muted-border bg-panel text-primary-text";

/** The Library's add-content buttons. */
const ADD_BUTTON =
  "flex h-9.5 shrink-0 items-center gap-2 rounded-md border border-primary-border bg-panel py-1 pr-3 pl-2 text-primary-text";

const formatTime = (iso: string, lang: string, withDay: boolean) =>
  new Date(iso).toLocaleString(lang, {
    month: withDay ? "short" : undefined,
    day: withDay ? "numeric" : undefined,
    hour: "numeric",
    minute: "2-digit",
  });

/** The left navigation, expanded, inside a project with its Library open. */
export const AppSidebar: Component<{ data: LibraryData }> = (props) => {
  const { t, currentLanguage } = useI18n();
  const link = (icon: Component<{ class?: string }>, label: string, opts?: { active?: boolean; nested?: boolean }) => (
    <span
      class={NAV_ROW}
      classList={{
        "pl-4": !opts?.nested,
        "pl-9": opts?.nested,
        "bg-panel-hover font-medium text-primary-text": opts?.active,
        "text-secondary-text": opts?.nested && !opts.active,
      }}
    >
      <Dynamic component={icon} class="size-4 shrink-0" aria-hidden="true" />
      {label}
    </span>
  );

  return (
    <div class="flex h-full w-50 shrink-0 flex-col space-y-2 border-e border-primary-border bg-panel p-2">
      <div class="flex h-9 items-center justify-between pr-2.5">
        <span class="px-4">
          <Logo class="h-5 w-auto" markClass="text-accent" textClass="text-primary-text" />
        </span>
        <span class="flex items-center">
          <TbSearch class="m-1.5 size-4" aria-hidden="true" />
          <TbLayoutSidebar class="m-1.5 size-4" aria-hidden="true" />
        </span>
      </div>

      <div class="mt-1 pb-1">
        <span class="flex min-h-9 w-full items-center gap-1.5 rounded-lg px-4 py-1.5">
          <TbCube class="size-4 shrink-0 text-muted-text" aria-hidden="true" />
          <span class="min-w-0 flex-1 truncate text-sm leading-4 font-medium text-primary-text">
            {props.data.project}
          </span>
          <TbChevronDown class="size-3.5 shrink-0 text-muted-text" aria-hidden="true" />
        </span>
      </div>

      <div class="border-t border-primary-border pb-1" />

      <span class={`${NAV_ROW} min-h-8 bg-brand pl-4 text-on-brand`}>
        <TbPlus class="size-4 shrink-0" aria-hidden="true" />
        {t("nav_new")}
      </span>

      <div class="flex flex-col pb-1">
        {link(TbListCheck, t("nav_chats"))}
        <span class="relative">
          {link(LuLibrary, t("project_homepage.library"), { active: true })}
          <TbChevronDown
            class="absolute top-1/2 right-3.5 size-3.5 -translate-y-1/2 rotate-180 text-muted-text"
            aria-hidden="true"
          />
        </span>
        {link(TbTag, t("entity.labels"), { nested: true })}
        {link(FaSolidGraduationCap, t("skills.title"))}
        {link(RiUserFacesUserStarLine, t("personas.page_title"))}
        {link(TbBolt, t("actions.title"))}
        {link(TbWaveSquare, t("nav_live_sessions"))}
      </div>

      <div class="border-t border-primary-border pb-1" />

      <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div class="mb-1 flex items-center justify-between gap-2">
          <span class="px-4 text-xs font-semibold tracking-wide text-secondary-text uppercase">
            {t("nav_recent_chats")}
          </span>
          <TbFilter class="m-1 size-4 text-muted-text" aria-hidden="true" />
        </div>
        <span class="px-4 pt-2 pb-0.5 text-xs font-medium text-muted-text">{t("nav_recent_today")}</span>
        <For each={props.data.tasks}>
          {(task) => (
            <span class="flex w-full items-center gap-2 rounded-md px-4 py-1.5 text-sm">
              <span class="min-w-0 flex-1 truncate">{task.title}</span>
              <span class="shrink-0 text-xs text-muted-text tabular-nums">
                {formatTime(task.at, currentLanguage(), false)}
              </span>
            </span>
          )}
        </For>
      </div>

      <div class="shrink-0">
        <div class="mb-2 flex items-center justify-center gap-1 border-t border-primary-border pt-2">
          <For each={[TbPuzzle, TbGift, TbHelp, TbDeviceDesktop, TbBell]}>
            {(icon) => <Dynamic component={icon} class="m-2 size-5 text-secondary-text" aria-hidden="true" />}
          </For>
        </div>
        <span class="flex items-center gap-3 rounded-lg px-4 py-2">
          <span
            class="grid size-8 shrink-0 place-items-center rounded-full text-sm font-medium text-white"
            style={{ background: props.data.user.color }}
          >
            {props.data.user.name[0]}
          </span>
          <span class="min-w-0 flex-1 truncate text-sm font-medium text-primary-text">{props.data.user.name}</span>
          <TbSettings class="size-5 shrink-0" aria-hidden="true" />
        </span>
      </div>
    </div>
  );
};

/**
 * The project's Library in its compact row view, mid-search: the whole list
 * until the query is in, then the rows whose names don't match close up, and
 * a new file lands on top while it is still processing.
 */
export const LibraryPage: Component<{ data: LibraryData; typed: string }> = (props) => {
  const { t } = useI18n();
  const matches = (file: LibraryFile) => file.name.toLowerCase().includes(props.data.query);

  return (
    <div class="flex h-full min-w-0 flex-col">
      <div class="mb-4 flex min-w-0 items-center gap-2 p-4 pb-0">
        <span class="flex min-w-0 shrink items-center gap-2 text-xl font-semibold text-primary-text">
          <LuLibrary class="size-6 shrink-0 text-brand" aria-hidden="true" />
          <span class="truncate">{t("project_homepage.library")}</span>
        </span>
        <span class="min-w-0 flex-1" />
        <span class="hidden shrink-0 items-center gap-1.5 text-sm text-brand @3xl:inline-flex">
          <TbInfoCircle class="size-4" aria-hidden="true" />
          {t("action.learn_more")}
        </span>
      </div>

      <div class="mb-4 flex flex-col gap-3 px-4 @3xl:flex-row @3xl:items-center">
        <span class="flex min-w-0 flex-1 items-center rounded-lg border border-primary-border bg-tile px-3 py-2 text-sm ring-2 ring-brand">
          <span class="truncate">{props.typed}</span>
          <span class="ms-px inline-block h-4 w-px bg-primary-text/70" />
        </span>
        <span class="flex shrink-0 items-center gap-1.5 self-start rounded-lg p-1.5 text-xs @3xl:self-auto">
          <TbFilterPlus class="size-3.5 stroke-[2.5px]" aria-hidden="true" />
          {t("action.add_filter")}
        </span>
      </div>

      <div class="mb-4 hidden px-4 @3xl:block">
        <div class="flex flex-wrap items-center gap-2 rounded-lg border border-primary-border bg-panel p-4">
          <span class={twMerge(ADD_BUTTON, "border-transparent bg-brand text-on-brand")}>
            <TbUpload class="size-4" aria-hidden="true" />
            <span class="text-base">{t("action.upload_files")}</span>
          </span>
          <span class={ADD_BUTTON}>
            <TbCopy class="size-4" aria-hidden="true" />
            <span class="text-base">{t("action.paste_content")}</span>
          </span>
          <span class={ADD_BUTTON}>
            <TbWorld class="size-4" aria-hidden="true" />
            <span class="text-base">{t("action.add_url")}</span>
          </span>
          <span class={ADD_BUTTON}>
            <TbMicrophone class="size-4" aria-hidden="true" />
            <span class="text-base">{t("action.record_audio")}</span>
          </span>
          <span class={`${ADD_BUTTON} text-xs`}>
            <span class="flex items-center">
              <For each={INTEGRATION_PROVIDERS}>
                {(provider, i) => (
                  <span
                    class="grid size-6 place-content-center rounded-full border border-muted-border bg-tile"
                    classList={{ "-ml-[0.4rem]": i() > 0 }}
                  >
                    <Dynamic component={INTEGRATION_PROVIDER_ICONS[provider.id]} class="size-4" />
                  </span>
                )}
              </For>
            </span>
            {t("integrations_connect")}
          </span>
        </div>
      </div>

      <div class="flex min-h-0 flex-1 flex-col gap-3 px-4">
        <div class="flex flex-wrap items-center gap-3 py-2">
          <span class="flex items-center justify-center gap-1.5 rounded-lg border border-muted-border bg-panel px-2 py-1 text-sm whitespace-nowrap text-primary-text dark:bg-tile">
            <TbSquare class="size-4 text-muted-text" aria-hidden="true" />
            {t("button_select_all")}
          </span>
          <span class="ml-auto hidden items-center gap-3 whitespace-nowrap @3xl:flex">
            <span class="text-sm text-secondary-text">{t("asset_source_filter_label")}</span>
            <span class={`${BUTTON} p-1.5 text-sm`}>
              <span class="inline-flex items-center">
                <For each={INGESTING_PROVIDERS}>
                  {(provider, i) => (
                    <span
                      class="grid size-4 place-content-center rounded-full border border-muted-border bg-tile"
                      classList={{ "-ml-1.5": i() > 0 }}
                    >
                      <Dynamic component={ASSET_SOURCE_ICONS[provider.assetSource.value]} class="size-2.5" />
                    </span>
                  )}
                </For>
              </span>
              {t("asset_source_filter_all")}
            </span>
            <span class="text-sm text-secondary-text">{t("label.sort_by")}</span>
            <span class={`${BUTTON} p-1.5 text-sm`}>{t("sort.newest")}</span>
            <span class="flex items-center gap-1 rounded-md border border-primary-border p-0.5">
              <TbLayoutList class="size-6 rounded bg-brand p-1 text-on-brand" aria-hidden="true" />
              <TbLayoutGrid class="size-6 rounded p-1 text-secondary-text" aria-hidden="true" />
            </span>
          </span>
        </div>

        <div class="flex flex-col">
          <div class="grow-in grid grid-rows-[1fr]" style={at(T.landed)}>
            <div class="min-h-0 overflow-hidden">
              <div class="row-slide pb-3" style={at(T.landed)}>
                <FileRow file={props.data.landed} status />
              </div>
            </div>
          </div>
          <For each={props.data.files}>
            {(file) => (
              <Show
                when={matches(file)}
                fallback={
                  <div class="collapse-out grid grid-rows-[0fr] opacity-0" style={at(T.filter)}>
                    <div class="min-h-0 overflow-hidden">
                      <div class="pb-3">
                        <FileRow file={file} />
                      </div>
                    </div>
                  </div>
                }
              >
                <div class="pb-3">
                  <FileRow file={file} />
                </div>
              </Show>
            )}
          </For>
        </div>
      </div>
    </div>
  );
};

/**
 * A Library row in compact preview mode. `status` gives it the lifecycle pill
 * a file wears for its first minute: processing, then ready.
 */
const FileRow: Component<{ file: LibraryFile; status?: boolean }> = (props) => {
  const { t, currentLanguage } = useI18n();
  const icon = () => ({ filename: props.file.filename ?? props.file.name });

  return (
    <div class="flex min-w-0 flex-col overflow-hidden rounded-xl border-2 border-primary-border bg-panel p-2">
      <div class="flex w-full items-center gap-2">
        <span class="shrink-0 p-1">
          <span class="block size-4 rounded border border-primary-border bg-tile dark:border-white/30" />
        </span>
        <span class="flex min-w-0 items-center gap-2 overflow-hidden @max-3xl:flex-1">
          <Dynamic
            component={getAssetIcon(icon())}
            class={`ml-1 size-6 shrink-0 ${getAssetIconColor(icon())}`}
            aria-hidden="true"
          />
          <Show when={props.file.source}>
            {(source) => (
              <span class="inline-flex size-4 shrink-0 items-center">
                <Dynamic component={ASSET_SOURCE_ICONS[source()]} class="size-full" />
              </span>
            )}
          </Show>
          <span class="flex min-w-0 items-center gap-2 overflow-hidden text-base font-medium">
            <span class="min-w-0 truncate">{props.file.name}</span>
            <Show when={props.file.filename}>
              <span class="min-w-0 shrink truncate text-xs font-normal text-muted-text">{props.file.filename}</span>
            </Show>
          </span>
        </span>
        <span class="hidden min-w-0 flex-1 items-center justify-end gap-1.5 @3xl:flex">
          <span class="flex min-w-0 items-center gap-1.5 overflow-hidden">
            <For each={props.file.labels}>
              {(label) => (
                <span class="flex shrink-0 items-center gap-1 rounded-full border border-primary-border bg-tile px-2 py-1 text-xs">
                  <span class="grid size-4 place-content-center rounded-full" style={{ background: label.color }}>
                    <Dynamic component={label.icon} class="size-3 text-white" />
                  </span>
                  {label.name}
                </span>
              )}
            </For>
          </span>
          <span class={`${BUTTON} shrink-0 p-1`}>
            <TbTag class="size-4" aria-hidden="true" />
          </span>
        </span>
        <span class="flex shrink-0 items-center gap-2">
          <Show when={props.status}>
            <span class="grid">
              <StatusPill state="PROCESSING" class="blip" style={at(T.landed + 0.1)}>
                <TbLoader2 class="size-3 shrink-0 opacity-90 motion-safe:animate-spin" aria-hidden="true" />
                {t("asset_lifecycle_short_processing")}
              </StatusPill>
              <StatusPill state="READY" class="pop-in" style={at(T.ready)}>
                <TbCheck class="size-3 shrink-0 text-green-600 dark:text-green-400" aria-hidden="true" />
                {t("asset_lifecycle_short_ready")}
              </StatusPill>
            </span>
          </Show>
          <span class="flex items-center gap-1">
            <span class="hidden shrink-0 text-xs whitespace-nowrap text-muted-text tabular-nums @3xl:inline">
              {formatTime(props.file.at, currentLanguage(), true)}
            </span>
            <span class={`${BUTTON} p-1`}>
              <TbDots class="size-4" aria-hidden="true" />
            </span>
          </span>
        </span>
      </div>
    </div>
  );
};

const StatusPill: ParentComponent<{ state: string; class: string; style: JSX.CSSProperties }> = (props) => (
  <span
    class={`col-start-1 row-start-1 inline-flex w-fit items-center gap-1 justify-self-end rounded px-1.5 py-0.5 text-xs font-medium ${assetLifecycleStatusPillClasses(props.state)} ${props.class}`}
    style={props.style}
  >
    {props.children}
  </span>
);

const noop = () => undefined;
const noopAsync = () => Promise.resolve(undefined);

/**
 * Project Memories as the side panel shows it, built from the app's own memory
 * components: collection on, newest first, a new memory arriving and being
 * pinned.
 */
export const MemoriesPanel: Component<{ data: LibraryData }> = (props) => {
  const { t } = useI18n();

  return (
    <div class="relative flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-primary-border bg-panel @3xl:pt-10">
      <span class="absolute top-2 left-2 hidden items-center gap-1 @3xl:flex">
        <span class={twMerge(BUTTON, "border-primary-border p-1.5 shadow-lg")}>
          <TbChevronsRight class="size-4" aria-hidden="true" />
        </span>
        <span class="flex items-center justify-center rounded-lg p-1.5">
          <TbArrowsDiagonal class="size-4" aria-hidden="true" />
        </span>
      </span>
      <div class="p-4 pb-0">
        <span class="mb-4 flex min-w-0 items-center gap-2 text-xl font-semibold text-primary-text">
          <TbBrain class="size-6 shrink-0 text-brand" aria-hidden="true" />
          <span class="truncate">{t("memory.title_project")}</span>
        </span>
        <p class="mb-2 hidden text-sm text-secondary-text @3xl:block">{t("memory.page.intro")}</p>
      </div>

      <div class="flex flex-col gap-4 p-4">
        <div class="hidden rounded-xl border border-primary-border bg-panel p-4 @3xl:block">
          <MemoryToggle label={t("memory.toggle_project")} checked onChange={noop} />
        </div>

        <div class="flex items-center justify-between gap-2">
          <div class="flex min-w-0 items-center gap-3">
            <span class="truncate text-lg font-medium text-primary-text">{t("memory.title_project")}</span>
            <span class="grid shrink-0">
              <span class="exit col-start-1 row-start-1" style={at(T.saved)}>
                <MemoryCountIndicator count={props.data.memoryCount} />
              </span>
              <span class="mark-in col-start-1 row-start-1" style={at(T.saved)}>
                <MemoryCountIndicator count={props.data.memoryCount + 1} />
              </span>
            </span>
          </div>
          <span class="flex shrink-0 items-center gap-2">
            <span class={twMerge(BUTTON, "hidden px-3 py-1.5 text-sm text-secondary-text @3xl:flex")}>
              <TbUpload class="size-4" aria-hidden="true" />
              {t("memory.import")}
            </span>
            <span class={twMerge(BUTTON, "border-transparent bg-brand px-3 py-1.5 text-sm text-on-brand")}>
              <TbPlus class="size-4" aria-hidden="true" />
              {t("memory.add")}
            </span>
          </span>
        </div>

        <span class="hidden items-center gap-2 rounded-lg border border-muted-border bg-canvas px-3 py-2 text-sm text-muted-text @3xl:flex">
          <TbSearch class="size-4 shrink-0" aria-hidden="true" />
          {t("memory.search.placeholder")}
        </span>

        <span class="flex items-center gap-3 px-1">
          <input type="checkbox" class="size-4 shrink-0 rounded border-muted-border accent-brand" />
          <span class="text-sm text-secondary-text">{t("memory.bulk.select_all")}</span>
        </span>

        <div class="flex flex-col">
          <div class="grow-in grid grid-rows-[1fr]" style={at(T.saved)}>
            <div class="min-h-0 overflow-hidden">
              <div class="card-appear pb-3" style={at(T.saved)}>
                <SavedMemory memory={props.data.saved} />
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-3">
            <For each={props.data.memories}>
              {(memory, i) => (
                <MemoryEntry
                  entry={{
                    id: `demo-memory-${i()}`,
                    scope: "project",
                    content: memory.text,
                    pinned: false,
                    createdBy: "",
                    createdAt: `${memory.date}T12:00:00Z`,
                    modifiedAt: `${memory.date}T12:00:00Z`,
                  }}
                  canEdit
                  onEdit={noop}
                  onArchive={noopAsync}
                  onDelete={noopAsync}
                  onPin={noop}
                  onUnpin={noop}
                  onToggleSelect={noop}
                />
              )}
            </For>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * The memory this scene saves, drawn as the app's memory entry so its pin can
 * be clicked on cue: the pinned edge and filled pin are what the app shows
 * once a memory is pinned.
 */
const SavedMemory: Component<{ memory: LibraryMemory }> = (props) => {
  const { currentLanguage } = useI18n();
  const ACTION = "rounded-lg border border-muted-border bg-panel p-1.5 text-muted-text";

  return (
    <div class="relative rounded-xl border border-primary-border bg-panel p-4">
      <span
        class="mark-in pointer-events-none absolute -inset-px rounded-xl border-s-2 border-brand"
        style={at(T.pin)}
      />
      <div class="flex items-start justify-between gap-3">
        <input type="checkbox" class="mt-1 size-4 shrink-0 rounded border-muted-border accent-brand" />
        <div class="min-w-0 flex-1">
          <p class="text-sm wrap-break-word whitespace-pre-wrap text-primary-text">{props.memory.text}</p>
          <p class="mt-2 text-xs text-muted-text">
            {new Date(`${props.memory.date}T12:00:00Z`).toLocaleDateString(currentLanguage(), {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </p>
        </div>
        <div class="flex shrink-0 items-center gap-1">
          <span class={`${ACTION} relative grid`}>
            <TbPin class="exit col-start-1 row-start-1 size-4" style={at(T.pin)} aria-hidden="true" />
            <TbPinFilled
              class="pop-in col-start-1 row-start-1 size-4 text-brand"
              style={at(T.pin)}
              aria-hidden="true"
            />
            <Cursor start={T.cursor} />
          </span>
          <span class={ACTION}>
            <TbEdit class="size-4" aria-hidden="true" />
          </span>
          <span class={ACTION}>
            <TbArchive class="size-4" aria-hidden="true" />
          </span>
          <span class={ACTION}>
            <TbTrash class="size-4" aria-hidden="true" />
          </span>
        </div>
      </div>
    </div>
  );
};
